import { useState } from "react";
import { INDUSTRIES, LANGUAGES, industryOption, isAdmin } from "../constants.js";
import { XIcon, PlusIcon } from "../Components/Icons";
import "./IdentityEditor.css";

export default function IdentityEditor({ identity, onChange, role }) {
  // Defensive normalization — a brand entry missing a nested field (an
  // older brand created before language/complianceText/complianceFontSize
  // existed, or a manually-edited config) would otherwise throw on access
  // below (e.g. identity.tone.formalCasual on an undefined tone), silently
  // freezing this whole component mid-render.
  const safe = {
    industry: null,
    language: "en",
    voiceExamples: [],
    usps: [],
    bannedWords: [],
    complianceNotes: null,
    complianceText: null,
    complianceFontSize: 0,
    palette: [],
    audiences: [],
    competitors: [],
    ...identity,
    tone: { formalCasual: 50, playfulSerious: 50, boldReserved: 50, ...identity?.tone },
    fonts: { heading: null, body: null, ...identity?.fonts },
  };

  function patch(fields) {
    onChange({ ...safe, ...fields });
  }

  return (
    <fieldset disabled={!isAdmin(role)} className="identity-editor">
      {!isAdmin(role) ? (
        <p className="identity-editor__readonly-notice">Only admins can edit brand identity.</p>
      ) : null}
      {/* Industry + language */}
      <section className="identity-editor__section">
        <div className="identity-editor__stack">
          <label className="identity-editor__field">
            <span>Industry <span className="identity-editor__required">*</span></span>
            <p className="identity-editor__field-desc">
              Where this brand operates. Steers phrasing, and regulated industries get automatic
              compliance lines in every generated asset.
            </p>
            <select
              value={safe.industry ?? ""}
              onChange={(e) => patch({ industry: e.target.value || null })}
            >
              <option value="">Not set</option>
              {INDUSTRIES.map((i) => (
                <option key={i.value} value={i.value}>
                  {i.label}
                </option>
              ))}
            </select>
            {industryOption(safe.industry)?.regulated ? (
              <p className="identity-editor__regulated-notice">
                Regulated industry: generated banners automatically include a compliance line
                (e.g. responsible-play wording for gambling) and avoid prohibited claims.
              </p>
            ) : null}
          </label>

          <label className="identity-editor__field">
            <span>Language <span className="identity-editor__required">*</span></span>
            <select value={safe.language} onChange={(e) => patch({ language: e.target.value })}>
              {LANGUAGES.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {/* Fonts */}
      <section className="identity-editor__section">
        <h2 className="identity-editor__section-title">Fonts</h2>
        <p className="identity-editor__section-desc">
          Family names as you'd write them in a brief — for creative guidance, not rendering. To
          actually use a font on banners, upload it as a reference (Brand → References → Font).
        </p>
        <div className="identity-editor__grid">
          <label className="identity-editor__field">
            <span>Heading font <span className="identity-editor__required">*</span></span>
            <input
              type="text"
              value={safe.fonts.heading ?? ""}
              onChange={(e) => patch({ fonts: { ...safe.fonts, heading: e.target.value || null } })}
              placeholder="e.g. Geist"
            />
          </label>
          <label className="identity-editor__field">
            <span>Body font <span className="identity-editor__required">*</span></span>
            <input
              type="text"
              value={safe.fonts.body ?? ""}
              onChange={(e) => patch({ fonts: { ...safe.fonts, body: e.target.value || null } })}
              placeholder="e.g. Inter"
            />
          </label>
        </div>
      </section>

      {/* Tone */}
      <section className="identity-editor__section">
        <h2 className="identity-editor__section-title">Tone</h2>
        <p className="identity-editor__section-desc">Where this brand's voice sits on each axis.</p>

        <ToneSlider
          leftLabel="Formal"
          rightLabel="Casual"
          value={safe.tone.formalCasual}
          onChange={(v) => patch({ tone: { ...safe.tone, formalCasual: v } })}
        />
        <ToneSlider
          leftLabel="Playful"
          rightLabel="Serious"
          value={safe.tone.playfulSerious}
          onChange={(v) => patch({ tone: { ...safe.tone, playfulSerious: v } })}
        />
        <ToneSlider
          leftLabel="Bold"
          rightLabel="Reserved"
          value={safe.tone.boldReserved}
          onChange={(v) => patch({ tone: { ...safe.tone, boldReserved: v } })}
        />
      </section>

      {/* Voice examples */}
      <section className="identity-editor__section">
        <h2 className="identity-editor__section-title">Voice examples</h2>
        <p className="identity-editor__section-desc">
          Real copy this brand loves or hates — steers generation toward/away from a style.
        </p>
        <VoiceExamplesField
          items={safe.voiceExamples}
          onChange={(voiceExamples) => patch({ voiceExamples })}
        />
      </section>

      {/* USPs */}
      <section className="identity-editor__section">
        <h2 className="identity-editor__section-title">USPs</h2>
        <p className="identity-editor__section-desc">Unique selling points to weave into copy.</p>
        <StringListField
          items={safe.usps}
          onChange={(usps) => patch({ usps })}
          placeholder="e.g. Free next-day delivery"
        />
      </section>

      {/* Banned words */}
      <section className="identity-editor__section">
        <h2 className="identity-editor__section-title">Banned words</h2>
        <p className="identity-editor__section-desc">Never used in generated copy.</p>
        <StringListField
          items={safe.bannedWords}
          onChange={(bannedWords) => patch({ bannedWords })}
          placeholder="e.g. cheap"
        />
      </section>

      {/* Compliance */}
      <section className="identity-editor__section">
        <h2 className="identity-editor__section-title">Compliance</h2>

        <label className="identity-editor__field identity-editor__field--full">
          <span>Compliance notes</span>
          <p className="identity-editor__field-desc">
            Free-text guidance for generation — not shown on the banner itself.
          </p>
          <textarea
            rows={3}
            value={safe.complianceNotes ?? ""}
            onChange={(e) => patch({ complianceNotes: e.target.value || null })}
            placeholder="e.g. Always mention returns policy applies to sale items too."
          />
        </label>

        <div className="identity-editor__grid identity-editor__grid--compliance">
          <label className="identity-editor__field">
            <span>Compliance text</span>
            <p className="identity-editor__field-desc">Rendered directly on every banner.</p>
            <input
              type="text"
              value={safe.complianceText ?? ""}
              onChange={(e) => patch({ complianceText: e.target.value || null })}
              placeholder="e.g. Offer valid while stocks last. T&Cs apply."
            />
          </label>

          <label className="identity-editor__field">
            <span>Compliance font size (px)</span>
            <p className="identity-editor__field-desc">0 = auto-size per banner.</p>
            <input
              type="number"
              min={0}
              max={80}
              value={safe.complianceFontSize}
              onChange={(e) => patch({ complianceFontSize: Number(e.target.value) || 0 })}
            />
          </label>
        </div>
      </section>

      {/* Palette */}
      <section className="identity-editor__section">
        <h2 className="identity-editor__section-title">
          Palette <span className="identity-editor__required">*</span>
        </h2>
        <p className="identity-editor__section-desc">Brand colors, used to theme generated banners.</p>
        <PaletteField items={safe.palette} onChange={(palette) => patch({ palette })} />
      </section>

      {/* Audiences */}
      <section className="identity-editor__section">
        <h2 className="identity-editor__section-title">Audiences</h2>
        <p className="identity-editor__section-desc">Segments generation can tailor copy toward.</p>
        <AudiencesField
          items={safe.audiences}
          onChange={(audiences) => patch({ audiences })}
        />
      </section>

      {/* Competitors */}
      <section className="identity-editor__section">
        <h2 className="identity-editor__section-title">Competitors</h2>
        <p className="identity-editor__section-desc">Named, for positioning and differentiation.</p>
        <StringListField
          items={safe.competitors}
          onChange={(competitors) => patch({ competitors })}
          placeholder="e.g. CompetitorCo"
        />
      </section>
    </fieldset>
  );
}

/* ---- Tone slider -------------------------------------------------------- */

function ToneSlider({ leftLabel, rightLabel, value, onChange }) {
  return (
    <div className="identity-editor__tone">
      <div className="identity-editor__tone-labels">
        <span>{leftLabel}</span>
        <span>{rightLabel}</span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="identity-editor__tone-slider"
      />
    </div>
  );
}

/* ---- Generic string list (USPs / banned words / competitors) ------------ */

function StringListField({ items, onChange, placeholder }) {
  const [draft, setDraft] = useState("");

  function add() {
    const value = draft.trim();
    if (!value) return;
    onChange([...items, value]);
    setDraft("");
  }

  function remove(index) {
    onChange(items.filter((_, i) => i !== index));
  }

  return (
    <div>
      {items.length > 0 ? (
        <ul className="identity-editor__list">
          {items.map((item, index) => (
            <li key={`${item}-${index}`} className="identity-editor__list-item">
              <span>{item}</span>
              <button
                type="button"
                onClick={() => remove(index)}
                aria-label={`Remove ${item}`}
                className="identity-editor__list-remove"
              >
                <XIcon className="identity-editor__icon" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="identity-editor__add-row">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          className="identity-editor__add-input"
        />
        {draft.trim() ? (
          <button type="button" onClick={add} className="identity-editor__add-btn">
            <PlusIcon className="identity-editor__icon" />
            Add
          </button>
        ) : null}
      </div>
    </div>
  );
}

/* ---- Voice examples ------------------------------------------------------ */

function VoiceExamplesField({ items, onChange }) {
  const [draftText, setDraftText] = useState("");
  const [draftLabel, setDraftLabel] = useState("love");

  function add() {
    const text = draftText.trim();
    if (!text) return;
    onChange([...items, { text, label: draftLabel }]);
    setDraftText("");
  }

  function remove(index) {
    onChange(items.filter((_, i) => i !== index));
  }

  return (
    <div>
      {items.length > 0 ? (
        <ul className="identity-editor__list">
          {items.map((item, index) => (
            <li key={`${item.text}-${index}`} className="identity-editor__list-item">
              <span
                className={`identity-editor__voice-badge identity-editor__voice-badge--${item.label}`}
              >
                {item.label}
              </span>
              <span className="identity-editor__voice-text">{item.text}</span>
              <button
                type="button"
                onClick={() => remove(index)}
                aria-label="Remove voice example"
                className="identity-editor__list-remove"
              >
                <XIcon className="identity-editor__icon" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="identity-editor__add-row">
        <select
          value={draftLabel}
          onChange={(e) => setDraftLabel(e.target.value)}
          className="identity-editor__voice-select"
        >
          <option value="love">Love</option>
          <option value="hate">Hate</option>
        </select>
        <input
          type="text"
          value={draftText}
          onChange={(e) => setDraftText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="e.g. Free shipping, no fine print."
          className="identity-editor__add-input"
        />
        {draftText.trim() ? (
          <button type="button" onClick={add} className="identity-editor__add-btn">
            <PlusIcon className="identity-editor__icon" />
            Add
          </button>
        ) : null}
      </div>
    </div>
  );
}

/* ---- Palette -------------------------------------------------------------- */

function PaletteField({ items, onChange }) {
  const [draft, setDraft] = useState("#2450ff");
  const isValidHex = /^#[0-9a-fA-F]{6}$/.test(draft);

  function add() {
    if (!isValidHex) return;
    onChange([...items, draft]);
  }

  function remove(index) {
    onChange(items.filter((_, i) => i !== index));
  }

  return (
    <div>
      {items.length > 0 ? (
        <ul className="identity-editor__list identity-editor__list--palette">
          {items.map((hex, index) => (
            <li key={`${hex}-${index}`} className="identity-editor__swatch-item">
              <span className="identity-editor__swatch" style={{ background: hex }} />
              <span>{hex}</span>
              <button
                type="button"
                onClick={() => remove(index)}
                aria-label={`Remove ${hex}`}
                className="identity-editor__list-remove"
              >
                <XIcon className="identity-editor__icon" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="identity-editor__add-row">
        <input
          type="color"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="identity-editor__color-input"
        />
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="#2450ff"
          className="identity-editor__add-input"
        />
        {isValidHex ? (
          <button type="button" onClick={add} className="identity-editor__add-btn">
            <PlusIcon className="identity-editor__icon" />
            Add
          </button>
        ) : null}
      </div>
    </div>
  );
}

/* ---- Audiences ------------------------------------------------------------ */

function AudiencesField({ items, onChange }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  function add() {
    const trimmedName = name.trim();
    if (!trimmedName) return;
    onChange([...items, { name: trimmedName, description: description.trim() }]);
    setName("");
    setDescription("");
  }

  function remove(index) {
    onChange(items.filter((_, i) => i !== index));
  }

  return (
    <div>
      {items.length > 0 ? (
        <ul className="identity-editor__list">
          {items.map((item, index) => (
            <li key={`${item.name}-${index}`} className="identity-editor__audience-item">
              <div>
                <p className="identity-editor__audience-name">{item.name}</p>
                {item.description ? (
                  <p className="identity-editor__audience-desc">{item.description}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => remove(index)}
                aria-label={`Remove ${item.name}`}
                className="identity-editor__list-remove"
              >
                <XIcon className="identity-editor__icon" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="identity-editor__add-row identity-editor__add-row--audience">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Audience name"
          className="identity-editor__add-input"
        />
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          className="identity-editor__add-input"
        />
        {name.trim() ? (
          <button type="button" onClick={add} className="identity-editor__add-btn">
            <PlusIcon className="identity-editor__icon" />
            Add
          </button>
        ) : null}
      </div>
    </div>
  );
}